/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { useStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

type Props = {
  productTypeItem?: React.ReactNode;
  keyInformationItems?: React.ReactNode[];
  pageCta?: React.ReactNode;
  navigationCta?: React.ReactNode;
};

const styles = {
  wrapper: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) 0;
    }
  `,
  productType: css`
    padding: var(--cds-spacing-200) 0;
  `,
  divider: css`
    border-top: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    margin: 0;
  `,
  items: css`
    display: flex;
    flex-direction: column;
    padding: var(--cds-spacing-100) 0;

    & > div {
      padding: var(--cds-spacing-100) 0;
    }

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
  pageCta: css`
    margin-bottom: var(--cds-spacing-200);
    width: 100%;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  navigationCta: css`
    border-top: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-200) 0 var(--cds-spacing-50);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
  streamLinedMWebNavigationCta: css`
    padding: var(--cds-spacing-100) 0;
  `,
  streamLinedMWebItems: css`
    margin-top: var(--cds-spacing-150);
    display: flex;
    flex-direction: column;

    & > div {
      padding-bottom: var(--cds-spacing-150);
    }
  `,
  streamLinedProductType: css`
    padding: var(--cds-spacing-150) 0;
  `,
  mWebWrapper: css`
    ${breakpoints.down('sm')} {
      :first-child {
        padding: 0 0 var(--cds-spacing-150) 0;
      }
    }
  `,
};

const KeyInformationModule = ({ productTypeItem, keyInformationItems, pageCta, navigationCta }: Props) => {
  const userAgent = useUserAgent();
  const isStreamlinedXDP = useStreamlinedXDP();

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <section css={matches && isStreamlinedXDP ? styles.mWebWrapper : styles.wrapper}>
          <div css={styles.productType}>{productTypeItem}</div>
          <hr css={styles.divider} />
          <div css={matches && isStreamlinedXDP ? styles.streamLinedMWebItems : styles.items}>
            {keyInformationItems}
          </div>
          {pageCta && <div css={styles.pageCta}>{pageCta}</div>}
          {navigationCta && (
            <div css={matches && isStreamlinedXDP ? styles.streamLinedMWebNavigationCta : styles.navigationCta}>
              {navigationCta}
            </div>
          )}
        </section>
      )}
    </Media>
  );
};

export default KeyInformationModule;
